import React, { FC } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { BasicContainer, BodyText, BodyTextSmall, FlexCol, FlexRow, H2Text, PrimaryButton } from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { PaywallRenewSubscriptionMutation, TrialType } from '@minecraft.graphql-operations';
import { useTranslation, Trans } from '@blocs.i18n';
import { PlanType } from 'components/Paywall/usePlans';
import { UsePayment } from 'components/Paywall/types';
import { gqlAccount } from '@minecraft.graphql-types';
import { PaywallLocation } from '../enums/PaywallLocation';
import { priceFormatter } from '../helpers/priceFormatter';
import { CARD_TYPES, PAYMENT_TYPES } from '../enums/PaymentMethods';

interface SuccessOrderSummaryProps {
  selectedPlan: PlanType;
  completedPurchase: UsePayment['completedPurchase'];
  currencyCode?: string;
  paywallLocation?: PaywallLocation;
  countryCode: gqlAccount['country']['code'];
  onPaymentCompleteRedirect?: (_?: string) => void;
  closeBillingPopup?: () => void;
}

export const SuccessOrderSummary: FC<SuccessOrderSummaryProps> = React.memo(
  ({
    selectedPlan,
    currencyCode,
    completedPurchase = {},
    paywallLocation,
    countryCode,
    onPaymentCompleteRedirect,
    closeBillingPopup,
  }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const { pathname: path } = location;
    const isMediaRoute = !!matchPath(path, {
      path: '/media-bank/:mediaType',
      exact: true,
      strict: false,
    });

    const { email, invoiceId } = completedPurchase;
    const { lastFour, paymentType, cardType } =
      completedPurchase?.paymentDetails ||
      (completedPurchase as unknown as PaywallRenewSubscriptionMutation['renewSubscription']);
    const billingCode = completedPurchase?.subscription?.plan?.billingCode ?? selectedPlan?.billingCode;
    const translationCode = selectedPlan?.translationCode ?? billingCode;
    const isApplePay = paymentType === PAYMENT_TYPES.APPLE_PAY;
    const cardIcon = isApplePay ? CARD_TYPES.ApplePay : CARD_TYPES[cardType];
    const hasCreditsApplied = completedPurchase?.creditsApplied > 0;
    const hasDiscount = completedPurchase?.discount > 0;
    const currency = completedPurchase?.currency?.code ?? currencyCode;
    const isTrialPlan = completedPurchase?.subscription?.trialEndsAt;
    const isCardlessTrial = completedPurchase?.subscription?.trialType === TrialType.Cardless;
    const shouldRenderPaymentMethod = completedPurchase?.total > 0 || isTrialPlan;
    const price = priceFormatter({
      price: completedPurchase?.items
        ? Number(completedPurchase?.total ?? 0) + Number(completedPurchase?.discount ?? 0)
        : completedPurchase?.subscription?.plan?.price ?? completedPurchase?.total,
      currencyCode: currency,
      countryCode,
      t,
    });
    const totalPrice = priceFormatter({
      price: completedPurchase?.total ?? 0,
      currencyCode: completedPurchase?.currency?.code ?? currencyCode,
      countryCode,
      t,
    });

    const trialLengthDays = selectedPlan?.trialLengthDays ?? 30;
    const chargeableItems = completedPurchase?.items?.filter((item: { price: number }) => item?.price > 0);

    const cardlessTrialFeatures = [
      'common:paywall.features.premium.plan.newRoles',
      'common:paywall.features.premium.roleSubmissions',
      'common:paywall.features.premium.storageUnlimited',
    ];

    return (
      <>
        <FlexCol w="full" mb="4" className="cn_atom_px-6" justifyContent="center" alignItems="center" gap="2">
          <FlexCol w="full" justifyContent="flex-start" alignItems="flex-start" maxW="md" className="cn_atom_py-4">
            <FlexCol w="full" justifyContent="flex-start" alignItems="flex-start" gap="4">
              <H2Text lineHeight="expanded">{t(`common:paywall.success.orderSummary`)}</H2Text>
              <BodyText data-qa-id="confirmation-email" as="div">
                <Trans
                  i18nKey="common:paywall.success.emailMessage"
                  values={{ email }}
                  components={[<BodyText fontWeight="medium"> </BodyText>]}
                />
              </BodyText>
              {shouldRenderPaymentMethod && (
                <>
                  {invoiceId && (
                    <BodyText lineHeight="expanded">
                      {t(`common:paywall.success.orderNumber`, { orderNumber: invoiceId })}
                    </BodyText>
                  )}
                  {lastFour && (
                    <FlexRow gap="2" alignItems="center">
                      <BodyText>{t(`common:paywall.success.paymentMethod`)}</BodyText>
                      {cardIcon && <Icon height={3} name={cardIcon} />}
                      <BodyText>{t(`common:paywall.success.cardEnding`, { lastFour })}</BodyText>
                    </FlexRow>
                  )}
                </>
              )}
              <BasicContainer w="full" className="mt-4" bgColor="paywallOrderDetail" padding={6} borderRadius="sm">
                <FlexCol gap="2">
                  <FlexRow data-qa-id="success-container-membership-summary" w="full" justifyContent="space-between">
                    <BodyText color="emphasis" fontWeight="semibold">
                      {t(`common:paywall.success.planMembership.${translationCode}`, {
                        count: chargeableItems?.length,
                      })}
                    </BodyText>
                    <BodyText color="emphasis" fontWeight="semibold">
                      {isTrialPlan ? totalPrice : price}
                    </BodyText>
                  </FlexRow>
                  {hasCreditsApplied && (
                    <FlexRow data-qa-id="success-container-credits-applied" w="full" justifyContent="space-between">
                      <BodyText color="emphasis" fontWeight="semibold">
                        {t('common:membership.subscription.creditsApplied')}
                      </BodyText>
                      <BodyText color="emphasis" fontWeight="semibold">
                        {priceFormatter({
                          price: -(completedPurchase?.creditsApplied ?? 0),
                          currencyCode: completedPurchase?.currency?.code ?? currencyCode,
                          countryCode,
                          t,
                        })}
                      </BodyText>
                    </FlexRow>
                  )}
                  {hasDiscount && (
                    <FlexRow w="full" justifyContent="space-between">
                      <BodyText color="emphasis" fontWeight="semibold">
                        {t('common:membership.subscription.discount')}
                      </BodyText>
                      <BodyText color="emphasis" fontWeight="semibold">
                        {priceFormatter({
                          price: -(completedPurchase?.discount ?? 0),
                          currencyCode: completedPurchase?.currency?.code ?? currencyCode,
                          countryCode,
                          t,
                        })}
                      </BodyText>
                    </FlexRow>
                  )}
                </FlexCol>
                <hr />
                <FlexRow data-qa-id="success-container-payment-total" w="full" justifyContent="space-between">
                  <BodyText color="emphasis" fontWeight="semibold">
                    {t('common:paywall.summary.total')}
                  </BodyText>
                  <BodyText color="emphasis" fontWeight="semibold">
                    {totalPrice}
                  </BodyText>
                </FlexRow>
              </BasicContainer>
              {paywallLocation === PaywallLocation.SIGNUP && (
                <FlexRow w="full" justifyContent="center">
                  <PrimaryButton
                    label={`${t('common:form.label.getStarted')}!`}
                    isSmall
                    onClick={() => onPaymentCompleteRedirect('login')}
                  />
                </FlexRow>
              )}
              {chargeableItems && (
                <FlexRow w="full" justifyContent="center">
                  <PrimaryButton
                    label={t('common:button.continue')}
                    isSmall
                    onClick={() => {
                      if (!isMediaRoute && chargeableItems?.length > 1) {
                        onPaymentCompleteRedirect('mediaBank');
                      }
                      closeBillingPopup?.();
                    }}
                  />
                </FlexRow>
              )}
            </FlexCol>
          </FlexCol>
        </FlexCol>
        {isTrialPlan && trialLengthDays > 0 && !isCardlessTrial && (
          <FlexCol
            justifyContent="center"
            alignItems="center"
            gap="2"
            borderColor="vanillaPaywall"
            borderSize={0.1}
            borderPlacement="all"
            borderRadius="sm"
            borderStyle="solid"
            w="full"
            m="auto"
            maxW="2xl"
            className="cn_atom_p-6"
          >
            <FlexCol className="cn_text-center" gap="2">
              <BodyTextSmall align="center" color="disabled" fontWeight="medium">
                {t('common:paywall.success.trial.cancelMessage', {
                  trialLengthDays,
                })}
              </BodyTextSmall>
              <BodyTextSmall align="center" color="disabled" fontWeight="medium">
                {t(`common:paywall.success.trial.${translationCode}.afterTrialCharge`, {
                  trialLengthDays,
                  price,
                })}
              </BodyTextSmall>
            </FlexCol>
          </FlexCol>
        )}
        {isTrialPlan && trialLengthDays > 0 && isCardlessTrial && (
          <FlexCol
            justifyContent="center"
            alignItems="center"
            gap="2"
            borderColor="default"
            borderSize={0.1}
            borderPlacement="all"
            borderStyle="solid"
            w="full"
            m="auto"
            maxW="2xl"
            className="cn_atom_p-4"
          >
            <FlexCol gap="2">
              <BodyTextSmall align="center" color="disabled" fontWeight="medium">
                {t('common:paywall.success.cardlessTrial.message')}
              </BodyTextSmall>
              <ul>
                {cardlessTrialFeatures.map((key, index) => (
                  <li key={key}>
                    <BodyTextSmall fontWeight="medium" as="span" testId={`cardless-features-${index + 1}`}>
                      <Trans i18nKey={key} components={[<BodyTextSmall fontWeight="bold" as="span" />]} />
                    </BodyTextSmall>
                  </li>
                ))}
              </ul>
              <BodyTextSmall align="center" color="disabled" fontWeight="medium">
                {t('common:paywall.success.cardlessTrial.upgradeMessage')}
              </BodyTextSmall>
            </FlexCol>
          </FlexCol>
        )}
      </>
    );
  }
);
